import type { JsonSchema } from '@jsonforms/core';
import { InstantStringSchema } from '@thinkalpha/common/schemas/codecs.js';
import { genericDefinitions, NewBasicOrder } from '@thinkalpha/trading-ws-client/contracts/order.js';
import type { ConditionalRule } from '@thinkalpha/trading-ws-client/util/conditional-schema.js';
import z from 'zod';

// Extended JsonSchema type that includes $defs (JSON Schema Draft 7+)
export type JsonSchemaWithDefs = JsonSchema & {
    $defs?: Record<string, unknown>;
};

/**
 * The override function used for JSON Schema generation.
 * Handles custom metadata like oneOf, errorMessage, x-* hints, conditionalRules.
 */
export function createJsonSchemaOverride() {
    return (ctx: { zodSchema: unknown; jsonSchema: unknown; path: (string | number)[] }) => {
        const zodSchema = ctx.zodSchema as z.ZodType;
        const meta = zodSchema.meta?.();
        if (!meta) return;

        const jsonSchema = ctx.jsonSchema as Record<string, unknown>;

        // Handle oneOf (convert anyOf to oneOf when flagged)
        if (meta.useOneOf && 'anyOf' in jsonSchema) {
            if (meta['oneOfItems'] && Array.isArray(meta['oneOfItems'])) {
                jsonSchema['oneOf'] = meta['oneOfItems'];
            } else {
                jsonSchema['oneOf'] = jsonSchema['anyOf'];
            }
            delete jsonSchema['anyOf'];
        }

        // Handle $id (for single-schema conversion)
        if (meta.$id) {
            jsonSchema['$id'] = meta.$id;
        }

        // Handle errorMessage (for ajv-errors on client)
        if (meta.errorMessage) {
            jsonSchema['errorMessage'] = meta.errorMessage;
        }

        // Handle default value
        if (meta['default'] !== undefined) {
            jsonSchema['default'] = meta['default'];
        }

        // Handle enum values (for conditional restrictions on non-oneOf schemas)
        if (meta['enumValues'] && Array.isArray(meta['enumValues'])) {
            jsonSchema['enum'] = meta['enumValues'];
        }

        // Handle x-* metadata for UI hints
        const xKeys = ['x-category', 'x-unit', 'x-hidden', 'x-symbology'] as const;
        for (const key of xKeys) {
            if (meta[key] !== undefined) {
                jsonSchema[key] = meta[key];
            }
        }

        // Handle conditional rules (inject into allOf)
        // TODO: Get declaration merging meta from David from trading-ws
        if ((meta.conditionalRules as any)?.length) {
            const existingAllOf = (jsonSchema['allOf'] as ConditionalRule[]) || [];
            jsonSchema['allOf'] = [...existingAllOf, ...(meta as any).conditionalRules];
        }

        // Remove internal-only metadata keys that AJV doesn't recognize
        delete jsonSchema['useOneOf'];
        delete jsonSchema['conditionalRules'];
        delete jsonSchema['oneOfItems'];
        delete jsonSchema['oneOfValuesAreNumeric'];
        delete jsonSchema['oneOfValuesAreIntegers'];
        delete jsonSchema['schemaKind'];
        delete jsonSchema['isRequired'];
        delete jsonSchema['enumValues'];
    };
}

export function getNewBasicOrderJsonSchema(): JsonSchemaWithDefs {
    const schema = z.toJSONSchema(NewBasicOrder, {
        io: 'input',
        override: createJsonSchemaOverride(),
        target: 'draft-7',
    }) as any;

    // z.toJSONSchema uses 'definitions' for draft-7, but we need '$defs'
    // Convert definitions to $defs for consistency with the rest of the codebase
    if (schema.definitions && !schema.$defs) {
        schema.$defs = schema.definitions;
        delete schema.definitions;
    }

    return schema as JsonSchemaWithDefs;
}

export const getGenericDefinitionsAsJsonSchemaDefinitions = (): JsonSchema['definitions'] => {
    // First pass: convert zod schemas to json schema
    const firstPass = Object.entries(genericDefinitions).reduce((acc, [key, value]) => {
        try {
            return {
                ...acc,
                [key]: z.toJSONSchema(value as any, {
                    io: 'input',
                    override: createJsonSchemaOverride(),
                    target: 'draft-7',
                }),
            };
        } catch {
            return { ...acc, [key]: value };
        }
    }, {} as JsonSchema);

    // Second pass: collect the top-level $id values from each definition
    const topLevelIds = new Set<string>();
    for (const [_key, schema] of Object.entries(firstPass)) {
        if (schema && typeof schema === 'object' && (schema as any).$id) {
            topLevelIds.add((schema as any).$id);
        }
    }

    // Third pass: replace nested $id with $ref for any $id that matches a top-level $id
    // Also convert #/definitions/ references to #/$defs/
    const convertNestedIdsToRefs = (obj: any, isTopLevel = false): any => {
        if (!obj || typeof obj !== 'object') {
            return obj;
        }

        // If this object has a $ref with #/definitions/, convert it to #/$defs/
        if (obj.$ref && typeof obj.$ref === 'string' && obj.$ref.startsWith('#/definitions/')) {
            obj = { ...obj, $ref: obj.$ref.replace('#/definitions/', '#/$defs/') };
        }

        // If this object has an $id that matches a top-level $id and we're NOT at the top level,
        // replace it with a $ref
        if (!isTopLevel && obj.$id && topLevelIds.has(obj.$id)) {
            return { $ref: obj.$id };
        }

        // Otherwise, recursively process the object
        if (Array.isArray(obj)) {
            return obj.map((item) => convertNestedIdsToRefs(item, false));
        }

        const result: any = {};
        for (const [key, value] of Object.entries(obj)) {
            result[key] = convertNestedIdsToRefs(value, false);
        }
        return result;
    };

    // Apply the conversion to each top-level definition
    const generic: JsonSchema['definitions'] = {};
    for (const [key, schema] of Object.entries(firstPass)) {
        generic[key] = convertNestedIdsToRefs(schema, true);
    }

    return {
        generic,
        Instant: z.toJSONSchema(InstantStringSchema, {
            io: 'input',
            override: createJsonSchemaOverride(),
            target: 'draft-7',
        }),
        // LocalDate codec emissions reference '#/$defs/LocalDate' from nested defs
        // (e.g. optionInstrument.expiry), so hoist it alongside Instant.
        LocalDate: z.toJSONSchema(z.iso.date(), {
            io: 'input',
            override: createJsonSchemaOverride(),
            target: 'draft-7',
        }),
    } as JsonSchema['definitions'];
};

/**
 * The $id zod emits on the root of the bundled basic order schema. Nothing references it: every
 * $ref in this widget is a JSON pointer path, and AJV throws when the same $id appears twice in
 * one compiled document. The bundled schema is embedded once per routeless venue, so the root
 * $id must not survive into the composed form schema.
 */
export const NEW_BASIC_ORDER_ID = '#/$defs/NewBasicOrder';

/**
 * Convert inline definitions with $id to $ref in schema properties.
 * When Zod generates JSON Schema, it sometimes inlines definitions with $id instead of using $ref.
 * This function converts those inline definitions back to $ref recursively using JSON.parse with a reviver.
 * Also converts 'definitions' to '$defs' for consistency with JSON Schema draft 2019-09+.
 * The root document keeps its body and loses its $id (see NEW_BASIC_ORDER_ID).
 */
export const convertInlineDefsToRefs = (schema: any): any => {
    if (!schema || typeof schema !== 'object') {
        return schema;
    }

    // Extract $defs or definitions (z.toJSONSchema uses 'definitions' for draft-7)
    const { $defs, definitions, ...rest } = schema;

    // Stringify and parse with a reviver to convert objects with $id to $ref
    // and convert #/definitions/ to #/$defs/
    const jsonString = JSON.stringify(rest);

    const processed = JSON.parse(jsonString, (_key, value) => {
        if (!value || typeof value !== 'object') return value;

        // $id takes precedence: a node carrying $id is a named definition that should
        // collapse to a $ref to that id, regardless of any nested $ref it also carries.
        // Newer codec emissions include both $id and a $ref to a sub-codec like Instant.
        if (value.$id && value.$id !== NEW_BASIC_ORDER_ID) {
            return { $ref: value.$id };
        }

        // The root basic order document is kept inline, minus its $id.
        if (value.$id === NEW_BASIC_ORDER_ID) {
            const { $id: _id, ...rest } = value;
            return rest;
        }

        // Otherwise, normalize legacy #/definitions/ refs to #/$defs/.
        if (typeof value.$ref === 'string' && value.$ref.startsWith('#/definitions/')) {
            return { ...value, $ref: value.$ref.replace('#/definitions/', '#/$defs/') };
        }

        return value;
    });

    // Only add $defs if it exists
    const defsValue = $defs ?? definitions;
    if (defsValue !== undefined) {
        return { $defs: defsValue, ...processed };
    }

    return processed;
};

/**
 * Remove repeated $id keys from a composed schema, keeping the first occurrence in document order.
 * AJV rejects a document in which one $id appears twice ("resolves to more than one schema"), and
 * the composed TradePro form schema merges definitions from the fetched executors schema with
 * bundled ones, so the same named definition can legitimately arrive from two sources. No $ref in
 * this widget depends on $id resolution (all refs are JSON pointer paths), so dropping the later
 * $id only removes the collision. The input is not mutated.
 */
export const dedupeSchemaIds = <T>(schema: T): T => {
    const seen = new Set<string>();

    const walk = (node: unknown): unknown => {
        if (!node || typeof node !== 'object') return node;
        if (Array.isArray(node)) return node.map(walk);

        const record = node as Record<string, unknown>;
        const result: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(record)) {
            if (key === '$id' && typeof value === 'string') {
                if (seen.has(value)) continue;
                seen.add(value);
            }
            result[key] = walk(value);
        }
        return result;
    };

    return walk(schema) as T;
};
